import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { motion } from 'framer-motion'
import { 
  FlaskConical, 
  MapPin, 
  ClipboardCheck,
  ShieldCheck,
  Clock,
  DollarSign,
  Star,
  ArrowRight,
  CheckCircle
} from 'lucide-react'
import api from '../utils/api'
import LabFinder from '../components/LabFinder'

// USA Flag SVG component - clean and works on all browsers
const USFlag = ({ className = "w-6 h-6" }) => (
  <svg 
    className={className} 
    viewBox="0 0 7410 3900" 
    xmlns="http://www.w3.org/2000/svg"
    aria-label="United States flag"
  >
    <rect width="7410" height="3900" fill="#b22234"/>
    <path d="M0,450H7410m0,600H0m0,600H7410m0,600H0m0,600H7410m0,600H0" stroke="#fff" strokeWidth="300"/>
    <rect width="2964" height="2100" fill="#3c3b6e"/>
    <g fill="#fff">
      <g id="s18">
        <g id="s9">
          <g id="s5">
            <g id="s4">
              <path id="s" d="M247,90 317.534230,307.082039 132.873218,172.917961H361.126782L176.465770,307.082039z"/>
              <use href="#s" y="420"/>
              <use href="#s" y="840"/>
              <use href="#s" y="1260"/>
            </g>
            <use href="#s" y="1680"/>
          </g>
          <use href="#s4" x="247" y="210"/>
        </g>
        <use href="#s9" x="494"/>
      </g>
      <use href="#s18" x="988"/>
      <use href="#s9" x="1976"/>
      <use href="#s5" x="2470"/>
    </g>
  </svg>
)

const Home = () => {
  const [popularTests, setPopularTests] = useState([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const fetchPopularTests = async () => {
      try {
        const response = await api.get('/tests?popular=true&limit=6')
        // API returns { success: true, data: [...] }
        const tests = response.data.data || response.data.tests || []
        setPopularTests(Array.isArray(tests) ? tests : [])
      } catch (error) {
        console.error('Error fetching popular tests:', error)
        setPopularTests([])
      } finally {
        setLoading(false)
      }
    }
    fetchPopularTests()
  }, [])

  const features = [
    {
      icon: DollarSign,
      title: 'Affordable Pricing',
      description: 'Save up to 85% compared to hospital prices. No hidden fees or surprise costs.'
    },
    {
      icon: MapPin,
      title: '4,500+ Lab Locations',
      description: 'Quest Diagnostics & Labcorp locations nationwide. Find one near you.'
    },
    {
      icon: Clock,
      title: 'Fast Results',
      description: 'Most results available within 1-3 business days via secure online portal.'
    },
    {
      icon: ShieldCheck,
      title: 'Private & Confidential',
      description: 'HIPAA compliant. Your health information is protected and secure.'
    }
  ]

  const steps = [
    {
      number: '1',
      title: 'Order Your Tests',
      description: 'Browse our comprehensive selection of lab tests and add them to your cart. No doctor visit or Prescription required.',
      icon: FlaskConical
    },
    {
      number: '2',
      title: 'Visit a Lab Near You',
      description: 'Choose from 4,500+ Quest Diagnostics or Labcorp locations. Walk in at your convenience.',
      icon: MapPin
    },
    {
      number: '3',
      title: 'Get Your Results',
      description: 'Receive your confidential results online within 1-3 business days.',
      icon: ClipboardCheck
    }
  ]

  const testimonials = [
    {
      name: 'Sarah M.',
      location: 'Austin, TX',
      text: 'So easy to use! I ordered my tests online, walked into the lab, and had my results in 2 days. Much cheaper than going through my doctor.',
      rating: 5
    },
    {
      name: 'James R.',
      location: 'Phoenix, AZ',
      text: 'I use Labs.ChatWithDr for my quarterly health checkups. The prices are unbeatable and the process is seamless.',
      rating: 5
    },
    {
      name: 'Maria L.',
      location: 'Chicago, IL',
      text: 'Finally, a service that makes lab testing accessible and affordable. The customer support team was incredibly helpful.',
      rating: 5
    }
  ]

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-primary-600 via-primary-700 to-primary-900 text-white overflow-hidden">
        <div className="absolute top-0 right-0 w-1/2 h-full bg-gradient-to-l from-primary-500/20 to-transparent"></div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24 relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left Column - Text Content */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              className="text-center lg:text-left"
            >
              <span className="inline-flex items-center gap-3 px-4 py-2 rounded-full bg-white/10 backdrop-blur-sm text-sm font-medium mb-6 border border-white/20">
                <span className="w-2 h-2 bg-white rounded-full animate-pulse"></span>
                <span className="flex items-center gap-2">
                  <ShieldCheck className="w-4 h-4" />
                  HIPAA Compliant • CLIA Certified Labs
                </span>
                <span className="inline-flex items-center gap-1.5 bg-white/20 rounded-full px-3 py-1">
                  <USFlag className="w-5 h-5" />
                  <span className="text-xs font-bold">USA</span>
                </span>
              </span>
              
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 leading-tight">
                Lab Tests Made
                <span className="text-primary-200"> Simple & Affordable</span>
              </h1>
              
              <ul className="text-lg md:text-xl text-primary-100 mb-8 max-w-xl space-y-2 text-left mx-auto lg:mx-0">
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-5 h-5 text-primary-200 flex-shrink-0 mt-1" />
                  <span>Order lab tests online – no doctor visit or prescription required</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-5 h-5 text-primary-200 flex-shrink-0 mt-1" />
                  <span>Visit a lab near you & get results fast</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-5 h-5 text-primary-200 flex-shrink-0 mt-1" />
                  <span>Save up to 85% compared to hospital prices</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-5 h-5 text-primary-200 flex-shrink-0 mt-1" />
                  <span>We accept credit, debit, HSA, and FSA cards</span>
                </li>
              </ul>

              {/* Proudly Serving USA */}
              <p className="text-lg text-primary-100 mb-8 flex items-center gap-2 justify-center lg:justify-start">
                <USFlag className="w-6 h-6" />
                Proudly serving patients nationwide
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
                <Link
                  to="/tests"
                  className="inline-flex items-center justify-center px-8 py-4 bg-white text-primary-600 rounded-xl font-semibold text-lg hover:bg-primary-50 transition-all shadow-lg hover:shadow-xl"
                >
                  Browse All Tests
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Link>
                <Link
                  to="/how-it-works"
                  className="inline-flex items-center justify-center px-8 py-4 bg-white/10 backdrop-blur-sm text-white rounded-xl font-semibold text-lg hover:bg-white/20 transition-all border border-white/20"
                >
                  How It Works
                </Link>
              </div>
            </motion.div>

            {/* Right Column - Image */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="relative hidden lg:flex justify-center"
            >
              <div className="relative">
                {/* Decorative background elements */}
                <div className="absolute -top-4 -right-4 w-48 h-48 bg-primary-400/20 rounded-full blur-3xl"></div>
                <div className="absolute -bottom-4 -left-4 w-40 h-40 bg-primary-300/20 rounded-full blur-3xl"></div>
                
                {/* Main image container - increased size */}
                <div className="relative rounded-2xl overflow-hidden shadow-2xl">
                  <img 
                    src="/assets/labtest.png" 
                    alt="Professional lab testing - Blood draw procedure" 
                    className="w-full h-auto rounded-xl max-w-md"
                  />
                </div>

                {/* Floating badge - Top Right - Results Ready */}
                <div className="absolute -top-3 -right-3 bg-white/20 backdrop-blur-sm rounded-xl shadow-xl p-3 border border-white/30">
                  <div className="flex items-center gap-2">
                    <div className="w-10 h-10 bg-white/30 rounded-lg flex items-center justify-center">
                      <CheckCircle className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <p className="text-white font-semibold text-xs">Results Ready</p>
                      <p className="text-green-300 text-xs font-medium">1-3 Business Days</p>
                    </div>
                  </div>
                </div>

                {/* Floating badge - Bottom Left - Lab Locations */}
                <div className="absolute -bottom-3 -left-3 bg-white/20 backdrop-blur-sm rounded-xl shadow-xl p-3 border border-white/30">
                  <div className="flex items-center gap-2">
                    <div className="w-10 h-10 bg-white/30 rounded-lg flex items-center justify-center">
                      <MapPin className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <p className="text-white font-semibold text-xs">4,500+ Labs</p>
                      <p className="text-primary-200 text-xs font-medium">Nationwide Coverage</p>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Features Bar Section */}
      <section className="py-6 bg-gray-50 border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-8">
            {features.map((feature, index) => (
              <div
                key={feature.title}
                className="flex items-center gap-3 p-3 lg:p-4"
              >
                <div className="w-10 h-10 bg-primary-100 rounded-xl flex items-center justify-center flex-shrink-0">
                  <feature.icon className="w-5 h-5 text-primary-600" />
                </div>
                <div className="min-w-0">
                  <h3 className="text-sm font-semibold text-gray-900 truncate">{feature.title}</h3>
                  <p className="text-xs text-gray-500 line-clamp-1 hidden sm:block">{feature.description.split('.')[0]}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works Section - Compact */}
      <section className="py-10 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8">
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2">
              How It Works
            </h2>
            <p className="text-gray-600">
              Get tested in 3 simple steps
            </p>
          </div>

          <div className="max-w-4xl mx-auto">
            <div className="grid md:grid-cols-3 gap-6">
              {steps.map((step, index) => (
                <div
                  key={step.number}
                  className="relative text-center"
                >
                  {/* Connecting line */}
                  {index < steps.length - 1 && (
                    <div className="hidden md:block absolute top-8 left-1/2 w-full h-0.5 bg-gradient-to-r from-primary-300 to-primary-100 z-0"></div>
                  )}
                  <div className="relative z-10">
                    {/* Icon with number badge */}
                    <div className="relative inline-block mb-4">
                      <div className="w-16 h-16 bg-gradient-to-br from-primary-500 to-primary-600 rounded-2xl flex items-center justify-center shadow-lg">
                        <step.icon className="w-8 h-8 text-white" />
                      </div>
                      <div className="absolute -top-1 -right-1 w-6 h-6 bg-primary-600 rounded-full flex items-center justify-center text-white font-bold text-xs shadow">
                        {step.number}
                      </div>
                    </div>
                    <h3 className="text-base font-semibold text-gray-900 mb-1">{step.title}</h3>
                    <p className="text-sm text-gray-600 line-clamp-2">{step.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="text-center mt-6">
            <Link
              to="/how-it-works"
              className="inline-flex items-center text-primary-600 font-medium text-sm hover:text-primary-700"
            >
              Learn More
              <ArrowRight className="w-4 h-4 ml-1" />
            </Link>
          </div>
        </div>
      </section>

      {/* Lab Finder Section */}
      <LabFinder />

      {/* Popular Tests Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Popular Lab Tests
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Our most requested tests at unbeatable prices
            </p>
          </div>

          {loading ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {[...Array(6)].map((_, i) => (
                <div key={i} className="bg-gray-100 rounded-2xl h-64 animate-pulse"></div>
              ))}
            </div>
          ) : popularTests.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-gray-500">No popular tests available at the moment.</p>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {popularTests.map((test, index) => (
                <motion.div
                  key={test._id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  viewport={{ once: true }}
                >
                  <Link
                    to={`/tests/${test.slug || test._id}`}
                    className="block bg-white rounded-2xl p-6 shadow-sm border border-gray-100 hover:shadow-lg hover:border-primary-200 transition-all group"
                  >
                    <div className="flex items-start justify-between mb-4">
                      <div className="w-12 h-12 bg-primary-100 rounded-xl flex items-center justify-center">
                        <FlaskConical className="w-6 h-6 text-primary-600" />
                      </div>
                      {test.retailPrice > test.ourPrice && (
                        <span className="bg-green-100 text-green-700 text-xs font-semibold px-3 py-1 rounded-full">
                          Save ${(test.retailPrice - test.ourPrice).toFixed(0)}
                        </span>
                      )}
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2 group-hover:text-primary-600 transition-colors">
                      {test.name}
                    </h3>
                    <p className="text-gray-600 text-sm mb-4 line-clamp-2">{test.description}</p>
                    <div className="flex items-center justify-between pt-4 border-t border-gray-100">
                      <div>
                        <span className="text-2xl font-bold text-primary-600">${test.ourPrice}</span>
                        {test.retailPrice > test.ourPrice && (
                          <span className="text-sm text-gray-400 line-through ml-2">${test.retailPrice}</span>
                        )}
                      </div>
                      <span className="text-primary-600 font-medium flex items-center">
                        View Details
                        <ArrowRight className="w-4 h-4 ml-1" />
                      </span>
                    </div>
                  </Link>
                </motion.div>
              ))}
            </div>
          )}

          <div className="text-center mt-12">
            <Link
              to="/tests"
              className="inline-flex items-center px-8 py-4 bg-primary-600 text-white rounded-xl font-semibold hover:bg-primary-700 transition-all shadow-lg hover:shadow-xl"
            >
              View All Tests
              <ArrowRight className="w-5 h-5 ml-2" />
            </Link>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              What Our Customers Say
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Join thousands of satisfied customers
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {testimonials.map((testimonial, index) => (
              <motion.div
                key={testimonial.name}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="bg-gray-50 rounded-2xl p-8 shadow-sm border border-gray-100"
              >
                <div className="flex mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <Star key={i} className="w-5 h-5 text-amber-400 fill-current" />
                  ))}
                </div>
                <p className="text-gray-600 mb-6 italic">"{testimonial.text}"</p>
                <div className="flex items-center">
                  <div className="w-12 h-12 bg-primary-100 rounded-full flex items-center justify-center">
                    <span className="text-primary-600 font-semibold">
                      {testimonial.name.split(' ').map(n => n[0]).join('')}
                    </span>
                  </div>
                  <div className="ml-4">
                    <p className="font-semibold text-gray-900">{testimonial.name}</p>
                    <p className="text-sm text-gray-500">{testimonial.location}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Trust Badges Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap items-center justify-center gap-8 md:gap-16">
            <div className="flex items-center gap-3 text-gray-600">
              <ShieldCheck className="w-10 h-10 text-green-600" />
              <div>
                <p className="font-semibold text-gray-900">HIPAA Compliant</p>
                <p className="text-sm">Your data is protected</p>
              </div>
            </div>
            <div className="flex items-center gap-3 text-gray-600">
              <CheckCircle className="w-10 h-10 text-blue-600" />
              <div>
                <p className="font-semibold text-gray-900">CLIA Certified</p>
                <p className="text-sm">Accredited laboratories</p>
              </div>
            </div>
            <div className="flex items-center gap-3 text-gray-600">
              <Clock className="w-10 h-10 text-purple-600" />
              <div>
                <p className="font-semibold text-gray-900">Fast Results</p>
                <p className="text-sm">1-3 business days</p>
              </div>
            </div>
            <div className="flex items-center gap-3 text-gray-600">
              <DollarSign className="w-10 h-10 text-emerald-600" />
              <div>
                <p className="font-semibold text-gray-900">Best Price</p>
                <p className="text-sm">Guaranteed savings</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-primary-600 to-primary-800 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-6">
              Ready to Take Control of Your Health?
            </h2>
            <p className="text-xl text-primary-100 mb-8 max-w-2xl mx-auto">
              Order your lab tests today and get results in as little as 24 hours.
            </p>
            <Link
              to="/tests"
              className="inline-flex items-center px-8 py-4 bg-white text-primary-600 rounded-xl font-semibold text-lg hover:bg-primary-50 transition-all shadow-lg hover:shadow-xl"
            >
              Get Started Now
              <ArrowRight className="w-5 h-5 ml-2" />
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  )
}

export default Home